import { createSelector } from "@reduxjs/toolkit";

// Base selector
const selectCollaborativeForm = (state) => state.collaborativeForm;

// Session selectors
export const selectSession = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.session
);

export const selectIsConnected = createSelector(
  [selectSession],
  (session) => session.isConnected
);

// Form data selectors
export const selectFormData = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm?.formData?.formStructure || {}
);

// Form status selector
export const selectFormStatus = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm?.formData?.status || "draft"
);

// Field state selectors
export const selectFieldStates = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.fieldStates
);

export const selectFieldState = createSelector(
  [selectFieldStates, (_, fieldPath) => fieldPath],
  (fieldStates, fieldPath) => fieldStates[fieldPath] || {}
);

export const selectFieldCombinedState = createSelector(
  [selectFieldState],
  (fieldState) => ({
    saving: fieldState.saving || false,
    saved: fieldState.saved || false,
    error: fieldState.error || null,
    conflict: fieldState.conflict || false,
    version: fieldState.version || 0,
  })
);

// Conflict selectors
export const selectConflicts = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.conflicts
);

export const selectConflict = createSelector(
  [selectConflicts, (_, fieldPath) => fieldPath],
  (conflicts, fieldPath) => conflicts[fieldPath] || null
);

// Pending changes selectors
export const selectPendingChanges = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.pendingChanges
);

export const selectPendingChangesCount = createSelector(
  [selectPendingChanges],
  (pendingChanges) => Object.keys(pendingChanges).length
);

// Section approval selectors
export const selectSectionApprovalStates = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.sectionApprovalStates
);

export const selectSectionApprovalState = createSelector(
  [selectSectionApprovalStates, (_, sectionId) => sectionId],
  (sectionApprovalStates, sectionId) =>
    sectionApprovalStates[sectionId] || "pending"
);

export const selectSectionCombinedState = createSelector(
  [selectSectionApprovalState, (_, sectionId) => sectionId],
  (approvalState, sectionId) => ({
    approvalState,
    isSubmitted: approvalState === "submitted",
    isApproved: approvalState === "approved",
    isRejected: approvalState === "rejected",
    isPending: approvalState === "pending",
  })
);

// Loading selectors
export const selectLoading = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.loading
);

export const selectFieldLoading = createSelector(
  [selectLoading, (_, fieldPath) => fieldPath],
  (loading, fieldPath) => loading.fieldUpdates[fieldPath] || false
);

export const selectConflictResolutionLoading = createSelector(
  [selectLoading, (_, conflictId) => conflictId],
  (loading, conflictId) => loading.conflictResolution[conflictId] || false
);

export const selectSectionSubmissionLoading = createSelector(
  [selectLoading, (_, sectionId) => sectionId],
  (loading, sectionId) => loading.sectionSubmission[sectionId] || false
);

export const selectSectionApprovalLoading = createSelector(
  [selectLoading, (_, sectionId) => sectionId],
  (loading, sectionId) => loading.sectionApproval[sectionId] || false
);

export const selectBatchUpdateLoading = createSelector(
  [selectLoading],
  (loading) => loading.batchUpdate || false
);

// Error selectors
export const selectErrors = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.errors
);

export const selectFieldError = createSelector(
  [selectErrors, (_, fieldPath) => fieldPath],
  (errors, fieldPath) => errors.fieldUpdates[fieldPath] || null
);

export const selectConflictResolutionError = createSelector(
  [selectErrors],
  (errors) => errors.conflictResolution
);

export const selectSectionSubmissionError = createSelector(
  [selectErrors],
  (errors) => errors.sectionSubmission
);

export const selectSectionApprovalError = createSelector(
  [selectErrors],
  (errors) => errors.sectionApproval
);

export const selectBatchUpdateError = createSelector(
  [selectErrors],
  (errors) => errors.batchUpdate
);

// Simple section save state selectors
export const selectSectionSaveStates = createSelector(
  [selectCollaborativeForm],
  (state) => state.sectionSaveStates || {}
);

export const selectSectionSaveState = (sectionId) =>
  createSelector(
    [selectSectionSaveStates],
    (sectionSaveStates) =>
      sectionSaveStates[sectionId] || {
        saving: false,
        saved: false,
        error: null,
      }
  );

// Product material data selector
export const selectProductMaterialData = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => collaborativeForm.productMaterialData || []
);

// Selector to get collaborative form data for a specific material
export const selectCollaborativeFormDataForMaterial = (materialId) =>
  createSelector([selectCollaborativeForm], (collaborativeForm) => {
    if (!collaborativeForm?.formData?.formStructure?.sections) {
      return null;
    }

    const sections = collaborativeForm.formData.formStructure.sections;

    // Convert sections object to array if needed
    const sectionsArray = Array.isArray(sections)
      ? sections
      : Object.values(sections);

    return sectionsArray.map((section, index) => {
      // Extract brandActions for Reformation approval status and notes (prioritize brandActions over legacy metadata)
      const sectionBrandActions = section.brandActions || {};
      const sectionMetadata = section.metadata || {}; // Fallback for legacy data
      
      return {
        section: section.title || `Section ${index + 1}`,
        vendorCompletionStatus: section.status || "pending",
        reformationApprovalStatus: sectionBrandActions.status || sectionMetadata.approval || sectionMetadata.status || "pending",
        reformationNote: sectionBrandActions.noteToVendor || sectionMetadata.notetovendor || sectionMetadata.requestNote || null,
      };
    });
  });

export const selectBasicInformationFromFormData = createSelector(
  [selectCollaborativeForm],
  (collaborativeForm) => {
    let result = {};
    if (
      collaborativeForm?.formData?.formStructure?.basicInformation?.fields
        .length > 0
    ) {
      result =
        collaborativeForm?.formData?.formStructure?.basicInformation?.fields.reduce(
          (acc, field) => {
            acc[field.id] = field;
            return acc;
          },
          {}
        );
    }
    return result;
  }
);
